import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ThumbsUp, ThumbsDown, Loader2 } from 'lucide-react';
import { submitFeedback, getFeedback } from '../services/feedbackService';
import type { FeedbackType } from '../types/feedback';
import { logger } from '../utils/logger';

interface FeedbackButtonsProps {
  messageId: string;
}

export function FeedbackButtons({ messageId }: FeedbackButtonsProps) {
  const { t } = useTranslation();
  const [currentFeedback, setCurrentFeedback] = useState<FeedbackType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // Load existing feedback on mount
  useEffect(() => {
    const loadFeedback = async () => {
      try {
        const { feedback, error } = await getFeedback(messageId);
        if (error) {
          logger.warn('⚠️ Error loading feedback:', error);
        } else if (feedback) {
          setCurrentFeedback(feedback.feedback_type);
        }
      } catch (error) {
        logger.error('❌ Exception loading feedback:', error);
      } finally {
        setIsInitializing(false);
      }
    };

    if (messageId) {
      loadFeedback();
    }
  }, [messageId]);

  const handleFeedbackClick = async (feedbackType: FeedbackType) => {
    // If clicking the same feedback type, do nothing (keep current state)
    if (currentFeedback === feedbackType) {
      return;
    }

    setIsLoading(true);
    try {
      const { success, error, feedback } = await submitFeedback(messageId, feedbackType);
      
      if (success && feedback) {
        setCurrentFeedback(feedback.feedback_type);
        logger.log('✅ Feedback updated:', feedbackType);
      } else {
        logger.error('❌ Failed to submit feedback:', error);
        // Optionally show error toast/notification here
      }
    } catch (error) {
      logger.error('❌ Exception submitting feedback:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render until initial load is complete
  if (isInitializing) {
    return (
      <div className="flex items-center gap-1.5">
        <div className="w-3 h-3" /> {/* Placeholder for spacing */}
        <div className="w-3 h-3" /> {/* Placeholder for spacing */}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      {/* Thumbs Up Button */}
      <button
        onClick={() => handleFeedbackClick('good')}
        disabled={isLoading}
        className={`flex items-center justify-center w-3 h-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
          currentFeedback === 'good'
            ? 'text-orange-500 hover:text-orange-600'
            : 'text-gray-500 hover:text-gray-600'
        }`}
        title={t('feedbackGood', '满意')}
        aria-label={t('feedbackGood', '满意')}
      >
        {isLoading && currentFeedback === 'good' ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : (
          <ThumbsUp className="w-3 h-3" />
        )}
      </button>

      {/* Thumbs Down Button */}
      <button
        onClick={() => handleFeedbackClick('bad')}
        disabled={isLoading}
        className={`flex items-center justify-center w-3 h-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
          currentFeedback === 'bad'
            ? 'text-orange-500 hover:text-orange-600'
            : 'text-gray-500 hover:text-gray-600'
        }`}
        title={t('feedbackBad', '不满意')}
        aria-label={t('feedbackBad', '不满意')}
      >
        {isLoading && currentFeedback === 'bad' ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : (
          <ThumbsDown className="w-3 h-3" />
        )}
      </button>
    </div>
  );
}

